import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GenieDocumentBrand, GenieDocumentTemplateKey } from "@/types/genie-documents/genie-document";

interface DocumentGeneratingStateProps {
  brand: GenieDocumentBrand;
  template: GenieDocumentTemplateKey;
}

const TEMPLATE_NOUN: Record<GenieDocumentTemplateKey, string> = {
  faq: "FAQ sheet",
  "how-to": "how-to guide",
  "one-pager": "one-pager",
};

/**
 * The "being summoned" state for document generation. Instead of a blank panel,
 * the customer's document assembles itself live in their brand colors — a
 * shimmering co-branded page taking shape — while status copy narrates the work.
 */
export const DocumentGeneratingState = ({ brand, template }: DocumentGeneratingStateProps) => {
  const primary = brand.primaryColor || "#4E9CFF";
  const secondary = brand.secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(135deg, ${primary}, ${secondary})`;

  const messages = [
    "Reading your knowledge…",
    `Drafting your ${TEMPLATE_NOUN[template]}…`,
    "Structuring the sections…",
    "Applying your brand…",
    "Polishing the details…",
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % messages.length), 2200);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const Bar = ({ w, pulse = true }: { w: string; pulse?: boolean }) => (
    <div className={`h-2.5 rounded-full bg-gray-100 ${pulse ? "animate-pulse" : ""}`} style={{ width: w }} />
  );

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
        {/* Brand shimmer sweep passing over the forming page */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10"
          style={{
            background: `linear-gradient(105deg, transparent 32%, ${primary}14 48%, ${secondary}14 56%, transparent 70%)`,
          }}
          animate={{ x: ["-120%", "120%"] }}
          transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Co-brand masthead — real brand mark, Help Genie present */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="h-14 w-14 rounded-xl shrink-0" style={{ background: gradient }} />
            <div className="space-y-2">
              <div className="h-3.5 w-32 rounded-full bg-gray-200 animate-pulse" />
              <div className="h-2.5 w-20 rounded-full bg-gray-100 animate-pulse" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 pt-0.5">
            <div className="h-2 w-14 rounded-full bg-gray-100" />
            <img src="/helpgenie.png" alt="" className="h-[18px] w-auto opacity-40" />
          </div>
        </div>

        {/* Gradient rule draws itself in */}
        <motion.div
          className="h-1.5 rounded-full mt-6"
          style={{ background: `linear-gradient(90deg, ${primary}, ${secondary})` }}
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 1, ease: "easeOut" }}
        />

        {/* Title */}
        <div className="mt-8 space-y-3">
          <div className="h-6 w-3/4 rounded-lg bg-gray-200 animate-pulse" />
          <div className="h-3 w-1/2 rounded-full bg-gray-100 animate-pulse" />
        </div>

        {/* Sections stagger in like they're being written */}
        <div className="mt-9 space-y-7">
          {[0, 1, 2].map((s) => (
            <motion.div
              key={s}
              className="flex gap-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + s * 0.28, duration: 0.5, ease: "easeOut" }}
            >
              <div
                className="h-8 w-8 rounded-lg shrink-0 flex items-center justify-center text-white text-sm font-bold"
                style={{ background: gradient }}
              >
                {s + 1}
              </div>
              <div className="flex-1 space-y-2.5 pt-1">
                <div className="h-3.5 w-2/5 rounded-full bg-gray-200 animate-pulse" />
                <Bar w="100%" />
                <Bar w="92%" />
                <Bar w="78%" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Narration */}
      <div className="mt-7 flex flex-col items-center">
        <div className="relative h-6 w-[280px]">
          <AnimatePresence mode="wait">
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 text-center text-[15px] font-medium text-gray-600"
            >
              {messages[i]}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="mt-3 flex gap-1.5">
          {[0, 1, 2].map((d) => (
            <motion.span
              key={d}
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: `linear-gradient(90deg, ${primary}, ${secondary})` }}
              animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1.15, 0.8] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: d * 0.18, ease: "easeInOut" }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
